import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Spinner, Button, Modal, Form, Table, Pagination, Tooltip, OverlayTrigger } from "react-bootstrap";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { toast } from "react-toastify";
import { fetchWithIap } from '../utils/iapService';
import logger from "../utils/logger";
import 'bootstrap-icons/font/bootstrap-icons.css';
import "./PromptLibrary.css";
import 'react-toastify/dist/ReactToastify.css';

/**
 * Parses the raw response text, specifically checking for structured SQL agent data,
 * and returns a sanitized HTML string ready for rendering.
 * Extracted so it can be used by both the Renderer and the Clipboard.
 * 
 * @param {string} responseText - The raw text or structured JSON from the bot response.
 * @returns {string} Sanitized HTML content.
 */
const getRenderedHTML = (responseText) => {
  if (!responseText) return '';

  const sqlAgentFlag = "$$SQL AGENT$$.";
  if (typeof responseText === 'string' && responseText.startsWith(sqlAgentFlag)) {
    const content = responseText.substring(sqlAgentFlag.length).trim();
    const jsonStartIndex = content.indexOf('[{');
    const jsonEndIndex = content.lastIndexOf('}]');

    if (jsonStartIndex !== -1 && jsonEndIndex > jsonStartIndex) {
      const preamble = content.substring(0, jsonStartIndex).trim();
      const jsonString = content.substring(jsonStartIndex, jsonEndIndex + 2);
      try {
        const jsonData = JSON.parse(jsonString);
        let tableHtml = `<table class="table table-bordered table-sm">`;
        // Headers
        const headers = Object.keys(jsonData[0] || {});
        tableHtml += '<thead><tr>';
        headers.forEach(header => {
          if (header.toLowerCase() !== 'file_url') {
            tableHtml += `<th>${header.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</th>`;
          }
        });
        tableHtml += '</tr></thead>';
        // Body
        tableHtml += '<tbody>';
        jsonData.forEach(row => {
          tableHtml += '<tr>';
          headers.forEach(header => {
            if (header.toLowerCase() !== 'file_url') {
              const cellValue = row[header] ?? '';
              const normalizedHeader = header.toLowerCase().replace(/_/g, ' ');
              if (normalizedHeader === 'document number' && row.file_url) {
                tableHtml += `<td><a href="${row.file_url}" target="_blank" rel="noopener noreferrer">${cellValue}</a></td>`;
              } else {
                tableHtml += `<td>${cellValue}</td>`;
              }
            }
          });
          tableHtml += '</tr>';
        });
        tableHtml += '</tbody></table>';
        const preambleHtml = preamble ? marked.parse(preamble) : '';
        return DOMPurify.sanitize(preambleHtml + tableHtml);
      } catch (e) {
        return DOMPurify.sanitize(marked.parse(content));
      }
    }
  }
  return DOMPurify.sanitize(marked.parse(responseText));
};

/**
 * A helper component to safely render complex HTML response text.
 * 
 * @param {Object} props - The component properties.
 * @param {string} props.responseText - The raw response string to be parsed and rendered.
 * @returns {JSX.Element} A div containing the sanitized HTML.
 */
const ResponseRenderer = ({ responseText }) => {
  return <div dangerouslySetInnerHTML={{ __html: getRenderedHTML(responseText) }} />;
};

/**
 * PromptLibrary Component
 * 
 * A full-page component that allows users to view, filter, sort, select, delete,
 * and export their bookmarked prompts. Designed to be rendered in a new detached
 * browser window via a React Portal (`NewWindowPortal`).
 * 
 * @param {Object} props - Component properties.
 * @param {Object} props.user - The currently authenticated user object.
 * @param {string} props.sessionId - The unique session ID used for API authorization.
 * @param {Function} props.onClose - Callback triggered when the library window needs to close.
 * @param {number} props.refreshKey - A state key used to force a re-fetch of bookmarks from the parent.
 * @param {Function} props.onBookmarkDeleted - Callback triggered when bookmarks are deleted to update parent state.
 * @returns {JSX.Element} The rendered PromptLibrary component.
 */
const PromptLibrary = ({ user, sessionId, onClose, refreshKey, onBookmarkDeleted }) => {
  const [allBookmarks, setAllBookmarks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ name: '', source: 'all', startDate: '', endDate: '' });
  const [sortState, setSortState] = useState({ direction: 'desc' });
  const [pagination, setPagination] = useState({ currentPage: 1, rowsPerPage: 10 });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const containerRef = useRef(null);
  const [targetContainer, setTargetContainer] = useState(null);

  /**
* Memoized value calculating today's date formatted as 'YYYY-MM-DD' based on the local timezone.
* Used to set the maximum allowed date boundary in the date filter inputs.
*/
  const todayStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  /**
  * A callback ref used to capture the main container's DOM node as soon as it mounts.
  * This is necessary for components like `react-hot-toast` and `react-bootstrap`'s Modal
  * to correctly attach themselves to the new window's DOM instead of the parent's.
  */
  const setContainerRef = useCallback((node) => {
    if (node !== null) {
      setTargetContainer(node);
    }
  }, []);

  /**
* Asynchronously fetches the full list of saved bookmarks from the backend API.
* Wrapped in `useCallback` to maintain a stable function reference for useEffect dependencies.
*/
  const fetchBookmarks = useCallback(async () => {
    const apiTag = 'GET /api/prompt_library';

    logger.info('Fetching bookmarks list.', { functionName: 'fetchBookmarks', api: apiTag });

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchWithIap('/api/prompt_library', {
        headers: { 'X-Session-ID': sessionId, 'Client-Type': 'Word' }
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ error: 'Failed to fetch bookmarks' }));
        logger.error('Failed to fetch bookmarks.', {
          functionName: 'fetchBookmarks',
          api: apiTag,
          status: response.status,
          errorPayload: errData
        });
        throw new Error(errData.error);
      }
      const data = await response.json();
      logger.info('Successfully fetched bookmarks.', {
        functionName: 'fetchBookmarks',
        count: data.length
      });
      setAllBookmarks(data);
    } catch (err) {
      logger.error('Exception during bookmark fetch.', {
        functionName: 'fetchBookmarks',
        originalError: err
      });
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, fetchWithIap]);

  /**
  * Effect hook to trigger the fetching of bookmarks.
  * Runs on initial component mount and whenever the `refreshKey` prop changes.
  */
  useEffect(() => {
    fetchBookmarks();
  }, [fetchBookmarks, refreshKey]);

  /**
   * A memoized computation that filters and sorts the bookmarks.
   * This is a performance optimization that prevents re-calculating the entire list
   * on every render, running only when the source data or filter/sort criteria change.
   */
  const filteredAndSortedBookmarks = useMemo(() => {
    let result = [...allBookmarks];
    result = result.filter(bm => {
      const nameMatch = !filters.name || (bm.full_name && bm.full_name.toLowerCase().includes(filters.name.toLowerCase()));
      let sourceMatch;
      const bmSource = (bm.data_source || '').toLowerCase();
      if (filters.source === 'all') sourceMatch = true;
      else if (filters.source === 'external') sourceMatch = ['external', 'fileupload', 'file_upload'].includes(bmSource);
      else sourceMatch = bmSource === filters.source;
      const bmDate = new Date((bm.bookmarked_at || '').replace('+00:00Z', ''));
      if (isNaN(bmDate.getTime())) return false; // Invalid date in data

      const startDate = filters.startDate ? new Date(`${filters.startDate}T00:00:00.000Z`) : null;
      const endDate = filters.endDate ? new Date(`${filters.endDate}T23:59:59.999Z`) : null;
      const dateMatch = (!startDate || bmDate >= startDate) && (!endDate || bmDate <= endDate);

      return nameMatch && sourceMatch && dateMatch;
    });
    result.sort((a, b) => {
      const timeA = new Date((a.bookmarked_at || '').replace('+00:00Z', '')).getTime();
      const timeB = new Date((b.bookmarked_at || '').replace('+00:00Z', '')).getTime();

      // Fallback to 0 if the date is invalid to prevent returning NaN
      const validTimeA = isNaN(timeA) ? 0 : timeA;
      const validTimeB = isNaN(timeB) ? 0 : timeB;

      return sortState.direction === 'desc' ? validTimeB - validTimeA : validTimeA - validTimeB;
    });
    return result;
  }, [allBookmarks, filters, sortState]);

  /**
  * A memoized computation that slices the filtered and sorted list into a
  * single page of data based on the current pagination settings.
  */
  const paginatedBookmarks = useMemo(() => {
    const startIndex = (pagination.currentPage - 1) * pagination.rowsPerPage;
    return filteredAndSortedBookmarks.slice(startIndex, startIndex + pagination.rowsPerPage);
  }, [filteredAndSortedBookmarks, pagination]);

  const totalPages = Math.ceil(filteredAndSortedBookmarks.length / pagination.rowsPerPage);

  /**
* Event handler for standard input changes across the filtering form.
* Updates the filter state and resets pagination back to the first page.
* 
* @param {React.ChangeEvent<HTMLInputElement|HTMLSelectElement>} e - The change event from the input field.
*/
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  /**
* Resets all filter criteria to their default/empty values and returns to page 1.
*/
  const handleClearFilters = () => {
    setFilters({ name: '', source: 'all', startDate: '', endDate: '' });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  /**
 * Toggles the sort direction for the 'Bookmarked Date' column.
 */
  const handleSort = () => {
    setSortState(prev => ({ direction: prev.direction === 'desc' ? 'asc' : 'desc' }));
  };

  /**
  * Navigates to a specific page number.
  */
  const handlePageChange = (pageNumber) => {
    setPagination(prev => ({ ...prev, currentPage: pageNumber }));
  };

  /**
 * Updates the limit of rows displayed per page and resets the view to the first page.
 * 
 * @param {React.ChangeEvent<HTMLSelectElement>} e - The change event from the rows-per-page dropdown.
 */
  const handleRowsPerPageChange = (e) => {
    setPagination({ currentPage: 1, rowsPerPage: parseInt(e.target.value, 10) });
  };

  /**
 * Toggles the selection state of an individual bookmark via its checkbox.
 * 
 * @param {string} id - The unique request_log_id of the bookmark to select/deselect.
 */
  const handleSelect = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  /**
 * Handles the "Select All" checkbox toggle in the table header.
 * Selects or deselects all bookmarks *currently visible on the page* that belong to the user.
 * 
 * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the select-all checkbox.
 */
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allVisibleOwnIds = paginatedBookmarks
        .filter(bm => bm.full_name === user.displayName)
        .map(bm => bm.request_log_id);
      setSelectedIds(new Set(allVisibleOwnIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  /**
* Triggers a backend process to compile the user's bookmarks into an Excel file,
* creates a temporary blob download link, and initiates the file download in the browser.
*/
  const handleExport = async () => {
    const apiTag = 'GET /api/prompt_library/export';
    logger.info('User initiated bookmark export.', { functionName: 'handleExport', api: apiTag });

    try {
      const response = await fetchWithIap('/api/prompt_library/export', {
        headers: { 'X-Session-ID': sessionId, 'Client-Type': 'Word' }
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ error: 'Failed to export bookmarks' }));
        logger.error('Export API failed.', {
          functionName: 'handleExport',
          api: apiTag,
          status: response.status,
          errorPayload: errData
        });
        throw new Error(errData.error);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition');
      const filenameMatch = disposition && disposition.match(/filename="?([^"]+)"?/);
      const filename = (filenameMatch && filenameMatch[1]) ? filenameMatch[1] : 'bookmarked_queries.xlsx';

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      logger.info('Export completed successfully.', { functionName: 'handleExport' });

      toast.success("Export successful!");
    } catch (err) {
      logger.error('Exception during export.', { functionName: 'handleExport', originalError: err });
      toast.error(err.message);
    }
  };

  /**
 * Deletes all currently selected bookmarks by sending their IDs to the backend API.
 * Updates local state, clears selections, and notifies the parent component upon success.
 */
  const handleDelete = async () => {
    const apiTag = 'DELETE /api/remove_bookmarks';
    if (selectedIds.size === 0) return;
    setIsDeleting(true);
    logger.info('User initiated bookmark deletion.', {
      functionName: 'handleDelete',
      api: apiTag,
      count: selectedIds.size
    });
    try {
      const response = await fetchWithIap('/api/remove_bookmarks', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          'Client-Type': 'Word',
        },
        body: JSON.stringify({ request_log_ids: Array.from(selectedIds) })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({ error: "Failed to delete" }));
        throw new Error(errData.error);
      }

      toast.success(`${selectedIds.size} bookmark(s) deleted.`);

      // Notify the parent App component about the deletions
      if (onBookmarkDeleted) {
        selectedIds.forEach(id => onBookmarkDeleted(id));
      }
      setSelectedIds(new Set());
      fetchBookmarks();
    } catch (err) {
      logger.error('Exception during deletion.', { functionName: 'handleDelete', originalError: err });
      toast.error(err.message);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  /**
* Copies the provided text or HTML content safely to the system clipboard.
* Specifically engineered to work across window portals, applying HTML structure and styling 
* to tables so they paste nicely into rich-text editors like Microsoft Word.
* 
* @param {string} text - The raw text or HTML content to be copied.
* @param {string} type - A label ('Prompt' or 'Response') used for the success toast message.
*/
  const copyToClipboard = async (text, type) => {
    const targetDoc = targetContainer ? targetContainer.ownerDocument : document;
    const targetWin = targetDoc.defaultView || window;

    // Check if rich-text copying is supported
    if (!targetWin.navigator || !targetWin.navigator.clipboard || !targetWin.navigator.clipboard.write) {
      // Fallback for older browsers (plain text only)
      try {
        const textArea = targetDoc.createElement("textarea");
        textArea.value = text;
        if (targetContainer) targetContainer.appendChild(textArea);
        else targetDoc.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        targetDoc.execCommand('copy');
        if (targetContainer) targetContainer.removeChild(textArea);
        else targetDoc.body.removeChild(textArea);
        toast.success(`${type} copied to clipboard!`);
      } catch (err) {
        toast.error(`Failed to copy ${type}. Browser restricted.`);
      }
      return;
    }

    try {
      let htmlContent = "";
      let plainText = "";

      // If it's a response, convert it to HTML and apply styles
      if (type === 'Response') {
        htmlContent = getRenderedHTML(text);

        // Add borders to tables like Main.jsx does
        if (htmlContent.includes('<table')) {
          logger.info('Table detected in content to be copied. Applying border styles.', { functionName: 'copyToClipboard' });
          htmlContent = htmlContent.replace(
            /<table[^>]*>/g,
            '<table border="1" style="border-collapse: collapse; width: 100%;">'
          );
        }

        // Get plain text alternative from the DOM
        const tempDiv = targetDoc.createElement("div");
        tempDiv.innerHTML = htmlContent;
        plainText = tempDiv.textContent || tempDiv.innerText || "";
      } else {
        // It's a prompt (pure text)
        plainText = text;
        htmlContent = `<p>${text.replace(/\n/g, '<br>')}</p>`;
      }

      const htmlBlob = new Blob([htmlContent], { type: "text/html" });
      const textBlob = new Blob([plainText], { type: "text/plain" });

      // Use targetWin.ClipboardItem to ensure cross-window portal support
      const clipboardItem = new targetWin.ClipboardItem({
        "text/html": htmlBlob,
        "text/plain": textBlob,
      });

      await targetWin.navigator.clipboard.write([clipboardItem]);

      logger.info('Content copied to clipboard via Clipboard API.', { functionName: 'copyToClipboard', type });
      toast.success(`${type} copied to clipboard!`);
    } catch (err) {
      logger.error('Copy via Clipboard API failed.', { functionName: 'copyToClipboard', originalError: err });
      console.error("Copy failed", err);
      toast.error(`Failed to copy ${type}.`);
    }
  };

  /**
* Formats a raw ISO date string into a user-friendly, localized date and time string.
* 
* @param {string} isoString - The raw date string from the database.
* @returns {string} The formatted date string, or 'N/A' / 'Invalid Date' if parsing fails.
*/
  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const parsableDateString = isoString.replace('Z', '');
    try {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: true, timeZone: 'UTC'
      }).format(new Date(parsableDateString));
    } catch {
      return 'Invalid Date';
    }
  };


  /**
   * Maps backend data source keys to human-readable display names.
   * 
   * @param {string} source - The raw source key (e.g., 'fileupload').
   * @returns {string} The nicely formatted label (e.g., 'File Upload').
   */
  const formatDataSourceName = (source) => {
    const sourceMap = {
      'llm': 'LLM', 'doclib': 'DocLib', 'external': 'File Upload',
      'fileupload': 'File Upload', 'file_upload': 'File Upload', 'romine': 'RoMine'
    };
    return sourceMap[(source || '').toLowerCase()] || source;
  };
  const ownBookmarksOnPageCount = paginatedBookmarks.filter(bm => bm.full_name === user.displayName).length;
  const isSelectAllChecked = ownBookmarksOnPageCount > 0 && selectedIds.size === ownBookmarksOnPageCount;

  if (isLoading) {
    return <div className="spinner-container"><Spinner animation="border" /> <p className="mt-3 text-muted">Fetching bookmarks...</p></div>;
  }
  if (error) {
    return <div className="error-container"><strong>Error:</strong> { }</div>;
  }

  return (
    <div className="prompt-library-container" ref={setContainerRef}>

      {/* Header */}
      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button variant="light" onClick={onClose} className="me-3"><i className="bi bi-arrow-left"></i></Button>
          <h2>Prompt Library</h2>
        </div>
        <Button variant="outline-success" onClick={handleExport}><i className="bi bi-download me-2"></i>Export</Button>
      </div>

      {/* Filters */}
      <div className="filter-section card card-body mb-3">
        <Form>
          <div className="row g-3 align-items-end">
            <div className="col-lg-3 col-md-6">
              <label htmlFor="name-filter" className="form-label">Medical Writer</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-person"></i></span>
                <Form.Control
                  type="text"
                  id="name-filter"
                  name="name"
                  placeholder="Filter by name..."
                  value={filters.name}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <label htmlFor="source-filter" className="form-label">Data Source</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-stack"></i></span>
                <Form.Select
                  id="source-filter"
                  name="source"
                  value={filters.source}
                  onChange={handleFilterChange}
                >
                  <option value="all">All Sources</option>
                  <option value="llm">LLM</option>
                  <option value="doclib">DocLib</option>
                  <option value="external">File Upload</option>
                  <option value="romine">RoMine</option>
                </Form.Select>
              </div>
            </div>
            <div className="col-lg-4 col-md-12">
              <label className="form-label">Date Range</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-calendar-range"></i></span>
                <Form.Control type="date" name="startDate" value={filters.startDate} max={todayStr} onChange={handleFilterChange} />
                <span className="input-group-text">to</span>
                <Form.Control type="date" name="endDate" value={filters.endDate} min={filters.startDate || undefined} max={todayStr} onChange={handleFilterChange} />
              </div>
            </div>
            <div className="col-lg-2 col-md-12">
              <Button variant="outline-secondary" className="w-100 clear-filters-btn" onClick={handleClearFilters}>
                <i className="bi bi-arrow-counterclockwise me-2"></i>Clear Filters
              </Button>
            </div>
          </div>
        </Form>
      </div>

      {/* Delete Button */}
      <div className="d-flex justify-content-end mb-2">
        <Button variant="danger" disabled={selectedIds.size === 0} onClick={() => setShowDeleteModal(true)}>
          <i className="bi bi-trash-fill me-2"></i>Delete ({selectedIds.size})
        </Button>
      </div>

      {/* Table */}
      <div className="table-responsive-container">
        <Table striped bordered hover className="prompt-library-table">
          <thead>
            <tr>
              <th style={{ width: '3%' }} className="text-center">
                {ownBookmarksOnPageCount === 0 ? (
                  // If there are no ownable bookmarks, wrap the disabled checkbox in an OverlayTrigger
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-disabled">You have no bookmarks to select on this page.</Tooltip>}
                    container={targetContainer}

                  >
                    <span className="d-inline-block">
                      <Form.Check
                        type="checkbox"
                        disabled
                        style={{ pointerEvents: 'none' }}
                      />
                    </span>
                  </OverlayTrigger>
                ) : (
                  // Otherwise, render the normal, enabled checkbox
                  <Form.Check
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={isSelectAllChecked}
                  />
                )}
              </th>
              <th style={{ width: '5%' }}>ID</th>
              <th style={{ width: '10%' }}>Medical Writer</th>
              <th style={{ width: '5%' }}>Data Source</th>
              <th style={{ width: '20%' }}>Prompt</th>
              <th>Response</th>
              <th style={{ width: '10%' }}>
                <button className="sort-btn" onClick={handleSort}>
                  <span>Bookmarked Date</span>
                  <i className={`bi ${sortState.direction === 'desc' ? 'bi-sort-down' : 'bi-sort-up'}`}></i>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedBookmarks.length > 0 ? (
              paginatedBookmarks.map((bm, index) => {
                const isOwner = bm.full_name === user.displayName;
                const sequentialId = (pagination.currentPage - 1) * pagination.rowsPerPage + index + 1;
                const checkbox = (
                  <Form.Check
                    type="checkbox"
                    disabled={!isOwner}
                    checked={selectedIds.has(bm.request_log_id)}
                    onChange={() => handleSelect(bm.request_log_id)}
                  />
                );
                return (
                  <tr key={bm.request_log_id}>
                    <td className="text-center">
                      {!isOwner ? (
                        <OverlayTrigger placement="top" overlay={<Tooltip>You can only select your own bookmarks.</Tooltip>}
                          container={targetContainer}
                        >
                          <span>{checkbox}</span>
                        </OverlayTrigger>
                      ) : checkbox}
                    </td>
                    <td>{sequentialId}</td>
                    <td>{bm.full_name}</td>
                    <td>{formatDataSourceName(bm.data_source)}</td>
                    <td className="prompt-col">
                      <div className="cell-content-wrapper">{bm.query}</div>
                      <Button variant="light" size="sm" className="copy-btn" onClick={() => copyToClipboard(bm.query, 'Prompt')}>
                        <i className="bi bi-clipboard"></i>
                      </Button>
                    </td>
                    <td className="response-col">
                      <div className="cell-content-wrapper">
                        <ResponseRenderer responseText={bm.response} />
                      </div>
                      <Button variant="light" size="sm" className="copy-btn" onClick={() => copyToClipboard(bm.response, 'Response')}>
                        <i className="bi bi-clipboard"></i>
                      </Button>
                    </td>
                    <td>{formatDate(bm.bookmarked_at)}</td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan="7" className="text-center text-muted py-4">
                  {allBookmarks.length === 0 ? "You have no bookmarked prompts." : "No bookmarks match the current filters."}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {paginatedBookmarks.length > 0 && (
        <div className="d-flex justify-content-end align-items-center mt-3 pagination-controls">
          <span className="me-3">Rows:</span>
          <Form.Select size="sm" value={pagination.rowsPerPage} onChange={handleRowsPerPageChange} style={{ width: 'auto' }} className="me-4">
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Form.Select>
          <span className="me-4">
            Page {pagination.currentPage} of {totalPages}
          </span>
          <Pagination size="sm" className="mb-0">
            <Pagination.Prev onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage === 1} />
            <Pagination.Next onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage === totalPages} />
          </Pagination>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered
        container={targetContainer}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to permanently delete these {selectedIds.size} bookmark(s)? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? <Spinner as="span" size="sm" animation="border" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PromptLibrary;
